import React, { useCallback, useEffect, useState, useRef } from 'react';
import CustomTypography from '../../CustomComponents/CustomTypography';
import PageTitle from '../../../Utils/PageTitle';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { DownloadOutlined } from '@ant-design/icons';
import { notification, Button } from 'antd';
import { TitleRow, ContainerRow } from '../styledComponents';
import { StyledRangePicker, StyledDivOptions } from '../Audit/styledComponents';
import AuditList from '../Audit/AuditList';
import AuditDetailPanel from '../Audit/AuditDetailPanel';
import { JOB_COLUMNS } from '../Audit/auditListColumns';
import { SplitShell, Splitter } from '../Audit/AuditDetailStyles';
import AuditColumnSettingsModal, {
  DEFAULT_JOB_VISIBLE_COLUMNS
} from '../Audit/AuditColumnSettingsModal';
import { fetchJobsForBillersAudit } from '../../../Apis/Biller';
import {
  getColumnPreferences,
  saveColumnPreferences
} from '../../../Apis/Clinic';

const BILLER_AUDIT_PAGE_NAME = 'BILLER_AUDIT_JOB_LIST';

const BILLER_EXCLUDED_COLUMNS = [
  'rejectedByReviewerTime',
  'submissionToReviewerLevel1Time',
  'currentLevelOfReview',
  'reviewerName'
];

// Jobs columns without the reviewer-specific columns and the Strike action.
const BILLER_COLUMNS = JOB_COLUMNS.filter(
  c => c.key !== 'strike' && !BILLER_EXCLUDED_COLUMNS.includes(c.key)
);

const BILLER_DEFAULT_VISIBLE_COLUMNS = {
  ...Object.fromEntries(
    Object.entries(DEFAULT_JOB_VISIBLE_COLUMNS).filter(
      ([key]) => !BILLER_EXCLUDED_COLUMNS.includes(key)
    )
  ),
  billStatus: true
};

const AUDIT_LIST_MIN_WIDTH = 280;
const AUDIT_LIST_MAX_WIDTH = 1200;

const BillerAudit = () => {
  const [theme] = useSelector(state => [state.themeReducer]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startTimeFilter: moment().subtract(2, 'months').startOf('day').valueOf(),
    endTimeFilter: moment().endOf('day').valueOf()
  });
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobData, setJobData] = useState([]);
  const [datePickerValue, setDatePickerValue] = useState([
    moment().subtract(2, 'months').startOf('day'),
    moment().endOf('day')
  ]);
  const [clearFiltersTrigger, setClearFiltersTrigger] = useState(0);
  const [exportFunction, setExportFunction] = useState(null);
  const [columnSettingsVisible, setColumnSettingsVisible] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(
    BILLER_DEFAULT_VISIBLE_COLUMNS
  );
  const [auditListWidth, setAuditListWidth] = useState(1000);

  const dateSelectionCompleted = useRef(false);
  const splitShellRef = useRef(null);

  const fetchAll = useCallback(() => {
    setLoading(true);
    const payload = {
      submitTimeFrom: dateRange ? dateRange.startTimeFilter : null,
      submitTimeTo: dateRange ? dateRange.endTimeFilter : null
    };
    fetchJobsForBillersAudit(payload)
      .then(response => {
        if (response.data.success) {
          setJobData(response.data.result);
        } else {
          notification.error({
            message: 'Fetch Failed',
            description: response.data.message
          });
        }
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.error('Error fetching biller audit jobs:', err);
      });
  }, [dateRange]);

  useEffect(() => {
    setSelectedJob(null);
    fetchAll();
  }, [fetchAll]);

  const fetchColumnPrefs = useCallback(async () => {
    try {
      const response = await getColumnPreferences(BILLER_AUDIT_PAGE_NAME);
      if (
        response.data?.result &&
        Array.isArray(response.data.result) &&
        response.data.result.length > 0
      ) {
        const prefs = response.data.result.reduce((acc, item) => {
          if (
            item.columnName &&
            item.columnName in BILLER_DEFAULT_VISIBLE_COLUMNS
          ) {
            acc[item.columnName] = item.selected;
          }
          return acc;
        }, {});
        setVisibleColumns({
          ...BILLER_DEFAULT_VISIBLE_COLUMNS,
          ...prefs,
          jobNumber: true,
          billStatus: true
        });
      } else {
        setVisibleColumns(BILLER_DEFAULT_VISIBLE_COLUMNS);
      }
    } catch {
      setVisibleColumns(BILLER_DEFAULT_VISIBLE_COLUMNS);
    }
  }, []);

  useEffect(() => {
    fetchColumnPrefs();
  }, [fetchColumnPrefs]);

  const handleSaveColumnSettings = newVisibleColumns => {
    setVisibleColumns(newVisibleColumns);
    const columnPreferencesArray = Object.keys(newVisibleColumns).map(
      columnName => ({ columnName, selected: newVisibleColumns[columnName] })
    );
    saveColumnPreferences(BILLER_AUDIT_PAGE_NAME, columnPreferencesArray)
      .then(() => {
        notification.success({
          message: 'Success',
          description: 'Column preferences saved successfully'
        });
      })
      .catch(() => {
        notification.error({
          message: 'Error',
          description:
            'Failed to save column preferences. Please try again.'
        });
      });
  };

  const disabledDate = current => current && current > moment().endOf('day');

  const dateRangeChangeHandler = date => {
    if (date) {
      dateSelectionCompleted.current = true;
      setDatePickerValue(date);
      setDateRange({
        startTimeFilter: parseFloat(date[0].startOf('day').format('x')),
        endTimeFilter: parseFloat(date[1].endOf('day').format('x'))
      });
    } else {
      dateSelectionCompleted.current = false;
      setDatePickerValue(null);
      setDateRange(null);
    }
  };

  const handleDatePickerOpenChange = open => {
    if (open) {
      dateSelectionCompleted.current = false;
      if (datePickerValue) setDatePickerValue(null);
    } else if (!dateSelectionCompleted.current && dateRange) {
      setDatePickerValue([
        moment(dateRange.startTimeFilter),
        moment(dateRange.endTimeFilter)
      ]);
    }
  };

  const startAuditListResize = e => {
    e.preventDefault();
    const onMove = ev => {
      const rect = splitShellRef.current?.getBoundingClientRect();
      if (!rect) return;
      const next = Math.min(
        AUDIT_LIST_MAX_WIDTH,
        Math.max(AUDIT_LIST_MIN_WIDTH, ev.clientX - rect.left)
      );
      setAuditListWidth(next);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (splitShellRef.current) {
        splitShellRef.current.classList.remove('resizing');
      }
    };
    if (splitShellRef.current) {
      splitShellRef.current.classList.add('resizing');
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <>
      <PageTitle title='Audit' />
      <ContainerRow>
        <TitleRow
          justify='space-between'
          style={{
            paddingLeft: 4,
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <CustomTypography color={theme['@text-color']}>
              Audit
            </CustomTypography>
          </div>
          <StyledDivOptions>
            <StyledRangePicker
              disabledDate={disabledDate}
              onChange={dateRangeChangeHandler}
              onOpenChange={handleDatePickerOpenChange}
              value={datePickerValue}
            />
            <Button onClick={() => setClearFiltersTrigger(t => t + 1)}>
              Clear Filters
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => exportFunction && exportFunction()}
            >
              Export to Excel
            </Button>
          </StyledDivOptions>
        </TitleRow>

        <SplitShell
          ref={splitShellRef}
          style={{ '--list-w': `${auditListWidth}px` }}
        >
          <AuditList
            columns={BILLER_COLUMNS}
            rows={jobData}
            getRowId={r => r.jobId}
            selectedId={selectedJob?.jobId}
            onSelect={setSelectedJob}
            visibleColumns={visibleColumns}
            onOpenColumnSettings={() => setColumnSettingsVisible(true)}
            onExportReady={fn => setExportFunction(() => fn)}
            clearFiltersTrigger={clearFiltersTrigger}
            loading={loading}
            storageKey='biller_audit_list'
            searchPlaceholder='Search jobs…'
            emptyText='No jobs match your search.'
            defaultSort={{ key: 'submissionTime', dir: 'desc' }}
            defaultFilters={{ billStatus: ['Billed'] }}
            exportName='biller_audit'
          />
          <Splitter
            role='separator'
            aria-orientation='vertical'
            onMouseDown={startAuditListResize}
          />
          <AuditDetailPanel
            session={selectedJob}
            onClose={() => setSelectedJob(null)}
          />
        </SplitShell>
      </ContainerRow>

      <AuditColumnSettingsModal
        visible={columnSettingsVisible}
        onCancel={() => setColumnSettingsVisible(false)}
        onSave={handleSaveColumnSettings}
        visibleColumns={visibleColumns}
        selectedTab={1}
        excludeColumns={BILLER_EXCLUDED_COLUMNS}
      />
    </>
  );
};

export default BillerAudit;
